import Decimal from "decimal.js";

/**
 * Defensive `Decimal` constructor. Returns `new Decimal(fallback)` for
 * nullish / empty / non-finite / unparseable input instead of throwing —
 * use when feeding user input or upstream API values into Decimal math.
 */
export const safeDecimal = (value?: string | number | null, fallback: Decimal.Value = "0") => {
  try {
    if (value === undefined || value === null || value === "") {
      return new Decimal(fallback);
    }
    const decimal = new Decimal(value);
    return decimal.isFinite() ? decimal : new Decimal(fallback);
  } catch {
    return new Decimal(fallback);
  }
};

/**
 * Parse a numeric text input into a `Decimal`, or `null` when it's empty /
 * non-finite / unparseable. Unlike {@link safeDecimal} this distinguishes
 * "no value" (null) from "zero" so callers can treat an empty field as unset.
 */
export const parseDecimalInput = (value?: string) => {
  if (!value || value.trim() === "") {
    return null;
  }

  try {
    const decimal = new Decimal(value);
    return decimal.isFinite() ? decimal : null;
  } catch {
    return null;
  }
};

/** Strip a numeric text input down to a valid number string (digits, one
 *  decimal point, single scientific-notation suffix, no leading zeros). */
export const sanitizeValue = (value: string): string => {
  return value
    .replace(/[^0-9.e+]/g, "") // Remove non-numeric characters except . e +
    .replace(/(\..*)\./g, "$1") // Remove extra decimal points
    .replace(/(e\+.*)(e\+)/gi, "$1") // Remove extra scientific notation
    .replace(/^0+(?=[1-9])/, "") // Remove leading zeros before non-zero digits (03 → 3, 001 → 1)
    .replace(/^0+(?=0\.)/, ""); // Remove leading zeros before 0. (00.3 → 0.3)
};
